import { ParsedBackgroundCheck, cleanResults, parseBackgroundCheck } from '@/backend/features/background-check/platform-functions/utils';
import { d550_backgroundCheck } from '@/backend/features/contact-management/defaults/d550_background_check';
import { requireManageMembersAndAnySystemPermission } from '@/backend/utils';
import { mapAccountSectionToAuditTag } from '@juvare/contact-management/backend/features/accounts/shared';
import { requireAnySystemPermission } from '@juvare/contact-management/backend/features/permissions/shared';
import { administratorAccountEmail } from '@juvare/credentialing/backend/constants';
import { AuthError, defineEntity } from '@juvare/platform-app/App';
import { AppState, UserState } from '@juvare/platform-sdk-react/types';
import {
    BackgroundCheckHistoryColumns,
    BackgroundCheckHistoryData,
    backgroundCheckHistoryColumns,
    backgroundCheckHistoryDataSchema,
} from '../../shared/entities/background-check-history-data/types';
import { BackgroundCheckStatus } from '../../types';
import { BackgroundCheckColumns, BackgroundCheckData, backgroundCheckEntityName } from '../background-check';

export const backgroundCheckHistoryEntity = defineEntity<BackgroundCheckHistoryData, BackgroundCheckHistoryColumns, AppState, UserState>(
    {
        version: 4,
        columns: backgroundCheckHistoryColumns,
        onMigrate: (params) => params.oldData,
        onTransformColumns: (_context, params) => {
            return {
                requestedBy: params.data.requestedBy,
                accountId: params.data.accountId,
                type: params.data.type,
                prevStatus: params.data.prevStatus,
                nextStatus: params.data.nextStatus,
                checkResponseByRequestId: params.data.checkResponseByRequestId,
                message: params.data.message,
                isUsingQuota: params.data.isUsingQuota,
                note: params.data.note,
                createdAt: params.data.createdAt ?? params.created,
                withDocument: Boolean(params.data.document?.url),
                isTestMode: params.data.isTestMode,
            };
        },
        onAuthGet: (_appState, userState, response) => {
            if (requireAnySystemPermission(userState, 'view-full-background-check-history')) {
                return response;
            } else if (requireManageMembersAndAnySystemPermission(userState, 'view-partial-background-check-history')) {
                return { ...response, data: { ...response.data, resultXml: undefined, request: undefined } };
            } else {
                throw new AuthError('Unauthorized');
            }
        },
        onAuthList: (_appState, userState, response) => {
            if (
                requireManageMembersAndAnySystemPermission(
                    userState,
                    'view-full-background-check-history',
                    'view-partial-background-check-history',
                )
            ) {
                return response;
            } else {
                throw new AuthError('Unauthorized');
            }
        },
        onAuthPost: (_appState, userState, params) => {
            // During client migration, we need to bypass standard authorization checks to manage data as needed.
            // This is only permitted for the administrator account.
            if (userState.email === administratorAccountEmail) {
                if (params.action === 'create' && params.data.responseXml && !params.data.parsedXml) {
                    let parsedXml: ParsedBackgroundCheck | undefined = undefined;

                    try {
                        parsedXml = parseBackgroundCheck(params.data.responseXml);
                    } catch (e) {
                        console.warn('There was an error when parsing background check');
                    }

                    params.data.parsedXml = parsedXml;
                    params.data.responseXml = cleanResults(params.data.responseXml);
                }

                return params.action === 'delete' ? params.prevData : params.data;
            }

            if (!requireManageMembersAndAnySystemPermission(userState, 'manage-background-check-data')) {
                throw new AuthError('Unauthorized');
            }

            if (params.action === 'create' || params.action === 'update') {
                params.data.requestedBy = userState.displayName;
                delete params.data.internalStatusChange;
                return params.data;
            }
            return;
        },
        onChangeSync: (context, params) => {
            const backgroundCheckStorage = context.storage<BackgroundCheckData, BackgroundCheckColumns>(backgroundCheckEntityName);

            if (params.action === 'create' && !params.data.internalStatusChange) {
                const handleStatusList: Array<BackgroundCheckStatus> = ['didNotConsent', 'formIncomplete', 'readyToSubmit'];
                const nextStatus = params.data.nextStatus;

                if (nextStatus && !handleStatusList.includes(nextStatus)) {
                    const backgroundCheckEntry = backgroundCheckStorage.list({
                        filter: [
                            {
                                column: 'accountId',
                                op: '=',
                                value: params.data.accountId,
                            },
                            {
                                column: 'type',
                                op: '=',
                                value: params.data.type,
                            },
                        ],
                    }).rows[0];

                    backgroundCheckStorage.post({
                        action: 'partial-update',
                        id: backgroundCheckEntry.id,
                        data: {
                            requestedBy: params.data.requestedBy,
                            internalBackgroundCheckChange: {
                                isManualChange: params.data.isManualChange,
                                type: params.data.type,
                                status: nextStatus,
                                updatedAt: Date.now(),
                            },
                        },
                        auditTags: [mapAccountSectionToAuditTag(d550_backgroundCheck)],
                    });
                }
            }
        },
    },
    backgroundCheckHistoryDataSchema,
);
